import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../context/LocalizationContext';

type Topic = {
  key: string;
  titleKey: string;
  contentKey: string;
  route: string;
  breadcrumbs: string[];
  stepKeys: string[];
};

type Message = {
  id: number;
  role: 'bot' | 'user';
  state?: 'thinking' | 'typing' | 'final';
  text?: string;
  topicKey?: string;
};

const topics: Topic[] = [
  {
    key: 'dashboard',
    titleKey: 'chatBotTopicDashboard',
    contentKey: 'chatBotTopicDashboardContent',
    route: '/',
    breadcrumbs: ['dashboard'],
    stepKeys: [
      'chatBotDashboardStep1',
      'chatBotDashboardStep2',
      'chatBotDashboardStep3',
      'chatBotDashboardStep4',
      'chatBotDashboardStep5',
    ],
  },
  {
    key: 'accountHolders',
    titleKey: 'chatBotTopicAccountHolders',
    contentKey: 'chatBotTopicAccountHoldersContent',
    route: '/account-holders',
    breadcrumbs: ['dashboard', 'accountHolders'],
    stepKeys: [
      'chatBotAccountHoldersStep1',
      'chatBotAccountHoldersStep2',
      'chatBotAccountHoldersStep3',
      'chatBotAccountHoldersStep4',
      'chatBotAccountHoldersStep5',
    ],
  },
  {
    key: 'expenses',
    titleKey: 'chatBotTopicExpenses',
    contentKey: 'chatBotTopicExpensesContent',
    route: '/expenses',
    breadcrumbs: ['dashboard', 'farmWork'],
    stepKeys: [
      'chatBotExpensesStep1',
      'chatBotExpensesStep2',
      'chatBotExpensesStep3',
      'chatBotExpensesStep4',
      'chatBotExpensesStep5',
    ],
  },
  {
    key: 'payments',
    titleKey: 'chatBotTopicPayments',
    contentKey: 'chatBotTopicPaymentsContent',
    route: '/payments',
    breadcrumbs: ['dashboard', 'payments'],
    stepKeys: [
      'chatBotPaymentsStep1',
      'chatBotPaymentsStep2',
      'chatBotPaymentsStep3',
      'chatBotPaymentsStep4',
      'chatBotPaymentsStep5',
    ],
  },
  {
    key: 'statements',
    titleKey: 'chatBotTopicStatements',
    contentKey: 'chatBotTopicStatementsContent',
    route: '/payments',
    breadcrumbs: ['dashboard', 'payments', 'statements'],
    stepKeys: [
      'chatBotStatementsStep1',
      'chatBotStatementsStep2',
      'chatBotStatementsStep3',
      'chatBotStatementsStep4',
      'chatBotStatementsStep5',
    ],
  },
  {
    key: 'settings',
    titleKey: 'chatBotTopicSettings',
    contentKey: 'chatBotTopicSettingsContent',
    route: '/admin-settings',
    breadcrumbs: ['settings', 'dashboardSettings'],
    stepKeys: [
      'chatBotSettingsStep1',
      'chatBotSettingsStep2',
      'chatBotSettingsStep3',
      'chatBotSettingsStep4',
      'chatBotSettingsStep5',
    ],
  },
];

const intentKeywords: Record<string, string[]> = {
  dashboard: ['dashboard', 'kontroleskerm', 'home', 'overview'],
  accountHolders: ['account holder', 'account holders', 'rekeninghouer', 'rekeninghouers', 'credit note', 'kredietnota', 'custom email'],
  expenses: ['expense', 'expenses', 'farm work', 'uitgawe', 'uitgawes', 'contribution', 'bydrae', 'equipment hiring', 'toerustinghuur'],
  payments: ['payment', 'payments', 'transaction', 'transaksie', 'account overview', 'betalings', 'import statement', 'trust transaction'],
  statements: ['statement', 'statements', 'state', 'send email', 'stuur e-pos', 'print', 'druk'],
  settings: ['settings', 'instellings', 'admin', 'user management', 'gebruikersbestuur', 'dashboard settings'],
};

const defaultSuggestions = [
  'chatBotSuggestionOne',
  'chatBotSuggestionTwo',
  'chatBotSuggestionThree',
];

// Suggestions shown when the user is on a specific page
const pageSuggestions: Record<string, string[]> = {
  '/': ['chatBotSuggestionDashboard', 'chatBotSuggestionTwo', 'chatBotSuggestionPayments'],
  '/account-holders': ['chatBotSuggestionTwo', 'chatBotSuggestionCreditNote', 'chatBotSuggestionThree'],
  '/expenses': ['chatBotSuggestionThree', 'chatBotSuggestionTwo', 'chatBotSuggestionPayments'],
  '/payments': ['chatBotSuggestionPayments', 'chatBotSuggestionOne', 'chatBotSuggestionTrustAccount'],
  '/admin-settings': ['chatBotSuggestionSettings', 'chatBotSuggestionUsers', 'chatBotSuggestionTrustAccount'],
  '/users': ['chatBotSuggestionUsers', 'chatBotSuggestionSettings', 'chatBotSuggestionTwo'],
};

// Follow-up suggestions shown after a guide card is answered
const followUpSuggestions: Record<string, string[]> = {
  dashboard: ['chatBotSuggestionTwo', 'chatBotSuggestionPayments', 'chatBotSuggestionOne'],
  accountHolders: ['chatBotSuggestionThree', 'chatBotSuggestionOne', 'chatBotSuggestionCreditNote'],
  expenses: ['chatBotSuggestionPayments', 'chatBotSuggestionOne', 'chatBotSuggestionTwo'],
  payments: ['chatBotSuggestionOne', 'chatBotSuggestionTrustAccount', 'chatBotSuggestionThree'],
  statements: ['chatBotSuggestionSettings', 'chatBotSuggestionTwo', 'chatBotSuggestionPayments'],
  settings: ['chatBotSuggestionTwo', 'chatBotSuggestionThree', 'chatBotSuggestionOne'],
};

interface GuideCardProps {
  topic: Topic;
  t: (key: string) => string;
  onNavigate: (route: string) => void;
}

const GuideCard: React.FC<GuideCardProps> = ({ topic, t, onNavigate }) => {
  const breadcrumbs = topic.breadcrumbs.map((key) => t(key)).join(' > ');

  return (
    <div className="chatbot-guide-card">
      <div className="chatbot-guide-title">{t(topic.titleKey)}</div>
      <p className="chatbot-guide-text">{t(topic.contentKey)}</p>

      <div className="chatbot-guide-section">
        <div className="chatbot-guide-label">{t('chatBotNavigationLabel')}</div>
        <div className="chatbot-guide-breadcrumbs">{breadcrumbs}</div>
        <div className="chatbot-guide-links">
          <button
            className="chatbot-nav-btn"
            type="button"
            onClick={() => onNavigate(topic.route)}
          >
            {t('chatBotOpenPage')}
          </button>
          <button
            className="chatbot-link-btn"
            type="button"
            onClick={() => onNavigate(topic.route)}
          >
            {topic.route}
          </button>
        </div>
      </div>

      <div className="chatbot-guide-section">
        <div className="chatbot-guide-label">{t('chatBotStepsLabel')}</div>
        <ol className="chatbot-guide-steps">
          {topic.stepKeys.map((stepKey) => (
            <li key={stepKey}>{t(stepKey)}</li>
          ))}
        </ol>
      </div>

      <div className="chatbot-guide-section">
        <div className="chatbot-guide-label">{t('chatBotScreenshotLabel')}</div>
        <div className="chatbot-screenshot">
          <div className="chatbot-screenshot-topbar" />
          <div className="chatbot-screenshot-content">
            <div className="chatbot-screenshot-nav" />
            <div className="chatbot-screenshot-main">
              <div className="chatbot-screenshot-main-row">
                <span className="chatbot-screenshot-chip">{t(topic.titleKey)}</span>
                <span className="chatbot-screenshot-chip chatbot-screenshot-chip-muted">{topic.route}</span>
              </div>
              <div className="chatbot-screenshot-card" />
              <div className="chatbot-screenshot-card chatbot-screenshot-card-secondary" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatBot: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const nextIdRef = useRef(2);
  const pendingBotTimersRef = useRef<number[]>([]);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    { id: 1, role: 'bot', text: t('chatBotWelcome'), state: 'final' },
  ]);
  const [suggestions, setSuggestions] = useState<string[]>(defaultSuggestions);
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(false);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  const topicMap = useMemo(() => {
    const map = new Map<string, Topic>();
    topics.forEach((topic) => map.set(topic.key, topic));
    return map;
  }, []);

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      pendingBotTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      pendingBotTimersRef.current = [];
    };
  }, []);

  // Update suggestions when the user navigates to a new page
  useEffect(() => {
    const pageSugs = pageSuggestions[location.pathname];
    if (pageSugs) {
      setSuggestions(pageSugs);
      setSuggestionsDismissed(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0) {
        return [{ id: 1, role: 'bot', text: t('chatBotWelcome'), state: 'final' }];
      }

      const first = prev[0];
      if (first.role === 'bot') {
        return [{ ...first, text: t('chatBotWelcome'), state: 'final' }, ...prev.slice(1)];
      }

      return prev;
    });
  }, [t]);

  const animateBotMessage = (text: string, topicKey?: string) => {
    const thinkingId = nextIdRef.current++;
    const thinkingDelay = 380;
    const guideComposeDelay = 520;
    const typingInterval = 16;

    setMessages((prev) => [...prev, { id: thinkingId, role: 'bot', state: 'thinking', text: '' }]);

    const startTypingTimer = window.setTimeout(() => {
      if (topicKey) {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === thinkingId
              ? { ...message, state: 'typing', text: '...' }
              : message
          )
        );

        const revealGuideTimer = window.setTimeout(() => {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === thinkingId
                ? { ...message, state: 'final', topicKey, text }
                : message
            )
          );
        }, guideComposeDelay);
        pendingBotTimersRef.current.push(revealGuideTimer);

        const followUps = followUpSuggestions[topicKey];
        if (followUps) {
          setSuggestions(followUps);
          setSuggestionsDismissed(false);
          setSuggestionsCollapsed(false);
        }

        return;
      }

      let charIndex = 0;
      setMessages((prev) =>
        prev.map((message) =>
          message.id === thinkingId ? { ...message, state: 'typing', text: '' } : message
        )
      );

      const typingTick = () => {
        charIndex += 1;
        const partial = text.slice(0, charIndex);

        setMessages((prev) =>
          prev.map((message) =>
            message.id === thinkingId
              ? {
                ...message,
                state: charIndex >= text.length ? 'final' : 'typing',
                text: partial,
              }
              : message
          )
        );

        if (charIndex < text.length) {
          const timer = window.setTimeout(typingTick, typingInterval);
          pendingBotTimersRef.current.push(timer);
        }
      };

      const firstTickTimer = window.setTimeout(typingTick, typingInterval);
      pendingBotTimersRef.current.push(firstTickTimer);
    }, thinkingDelay);

    pendingBotTimersRef.current.push(startTypingTimer);
  };

  const addUserMessage = (text: string) => {
    const id = nextIdRef.current++;
    setMessages((prev) => [...prev, { id, role: 'user', text }]);
  };

  const findTopicByQuestion = (question: string): Topic | null => {
    const normalized = question.toLowerCase();

    for (const [topicKey, keywords] of Object.entries(intentKeywords)) {
      const matches = keywords.some((keyword) => normalized.includes(keyword));
      if (matches) {
        return topicMap.get(topicKey) ?? null;
      }
    }

    return null;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const question = input.trim();
    if (!question) {
      return;
    }

    addUserMessage(question);
    setInput('');

    const matchedTopic = findTopicByQuestion(question);
    if (matchedTopic) {
      animateBotMessage(t(matchedTopic.contentKey), matchedTopic.key);
      return;
    }

    animateBotMessage(t('chatBotUnknown'));
  };

  const askSuggestion = (key: string) => {
    const suggestion = t(key);
    addUserMessage(suggestion);

    const matchedTopic = findTopicByQuestion(suggestion);
    if (matchedTopic) {
      animateBotMessage(t(matchedTopic.contentKey), matchedTopic.key);
      return;
    }

    animateBotMessage(t('chatBotUnknown'));
  };

  const handleClearChat = () => {
    pendingBotTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    pendingBotTimersRef.current = [];
    nextIdRef.current = 2;
    setMessages([{ id: 1, role: 'bot', text: t('chatBotWelcome'), state: 'final' }]);
    const pageSugs = pageSuggestions[location.pathname] ?? defaultSuggestions;
    setSuggestions(pageSugs);
    setSuggestionsDismissed(false);
    setSuggestionsCollapsed(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <button
        className="chatbot-fab"
        onClick={open ? handleClose : handleOpen}
        aria-label={t('chatBotLabel')}
        title={t('chatBotLabel')}
      >
        {open ? 'X' : 'AI'}
      </button>

      {open && (
        <div className="chatbot-panel" role="dialog" aria-label={t('chatBotTitle')}>
          <div className="chatbot-header">
            <span className="chatbot-header-title">{t('chatBotTitle')}</span>
            <div className="chatbot-header-actions">
              <button
                className="chatbot-clear-btn"
                onClick={handleClearChat}
                type="button"
              >
                {t('chatBotClear')}
              </button>
              <button
                className="chatbot-close-btn"
                onClick={handleClose}
                aria-label={t('close')}
                type="button"
              >
                X
              </button>
            </div>
          </div>

          <div className="chatbot-body">
            <div className="chatbot-messages" aria-live="polite">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`chatbot-message ${message.role === 'user' ? 'chatbot-message-user' : 'chatbot-message-bot'} ${message.role === 'bot' && message.topicKey ? 'chatbot-message-guide' : ''}`}
                >
                  {message.role === 'bot' && message.state === 'thinking' ? (
                    <span className="chatbot-thinking" aria-label="thinking">
                      <span className="chatbot-thinking-dot" />
                      <span className="chatbot-thinking-dot" />
                      <span className="chatbot-thinking-dot" />
                    </span>
                  ) : null}
                  {message.role === 'bot' && message.topicKey ? (
                    <GuideCard
                      topic={topicMap.get(message.topicKey) as Topic}
                      t={t}
                      onNavigate={(route) => navigate(route)}
                    />
                  ) : (
                    <>
                      {message.text}
                      {message.role === 'bot' && message.state === 'typing' && !message.topicKey ? <span className="chatbot-typing-caret" /> : null}
                    </>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {!suggestionsDismissed && (
              <div className="chatbot-suggestions-wrap">
                <div className="chatbot-suggestions-header">
                  <p className="chatbot-prompt">{t('chatBotSuggestions')}</p>
                  <div className="chatbot-suggestions-controls">
                    <button
                      type="button"
                      className="chatbot-suggestions-ctrl-btn"
                      onClick={() => setSuggestionsCollapsed((prev) => !prev)}
                      title={suggestionsCollapsed ? t('chatBotShowSuggestions') : t('chatBotMinimizeSuggestions')}
                      aria-label={suggestionsCollapsed ? t('chatBotShowSuggestions') : t('chatBotMinimizeSuggestions')}
                    >
                      {suggestionsCollapsed ? '+' : '–'}
                    </button>
                    <button
                      type="button"
                      className="chatbot-suggestions-ctrl-btn"
                      onClick={() => setSuggestionsDismissed(true)}
                      title={t('chatBotDismissSuggestions')}
                      aria-label={t('chatBotDismissSuggestions')}
                    >
                      ×
                    </button>
                  </div>
                </div>
                {!suggestionsCollapsed && (
                  <div className="chatbot-topics">
                    {suggestions.map((key) => (
                      <button key={key} className="chatbot-topic-btn" onClick={() => askSuggestion(key)} type="button">
                        {t(key)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form className="chatbot-input-row" onSubmit={handleSubmit}>
              <input
                className="chatbot-input"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder={t('chatBotInputPlaceholder')}
                aria-label={t('chatBotInputPlaceholder')}
              />
              <button className="chatbot-send-btn" type="submit">
                {t('chatBotSend')}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
